import { REQUEST_OPTIONS } from '../signup/signup-flow/SignupFlow'

const UBER_SSO_URL =
  'https://us-central1-infra-test-308817.cloudfunctions.net/new_uber_sso'

enum SSOError {
  INVALID_IN_AUTH_SESSION_ID = 'INVALID_IN_AUTH_SESSION_ID',
  INVALID = 'INVALID',
  MOBILE_INVALID = 'MOBILE_INVALID',
  SMS_OTP_TOO_MANY_REQUESTS = 'SMS_OTP_TOO_MANY_REQUESTS',
  SMS_OTP_INCORRECT = 'SMS_OTP_INCORRECT',
  EMAIL_OTP_INCORRECT = 'EMAIL_OTP_INCORRECT',
  PASSWORD_INCORRECT = 'PASSWORD_INCORRECT',
}

/**
 * Source of truth for determining the correct error message to display after
 * an unsuccessful SSO request.
 */
export const parseRequestError = (
  statusCode: number,
  data: any,
  defaultErrorMessage: string,
  emailHint: string | undefined
): string => {
  switch (statusCode) {
    case 200:
      console.error('Trying to parse error out of a successful request.')
      return ''
    case 400:
      const sessionIdError = data.content?.errorType
      const ssoErrors = data.content?.screenErrors?.[0]?.errors

      if (sessionIdError === SSOError.INVALID_IN_AUTH_SESSION_ID) {
        return 'Session has expired. Please refresh the page!'
      } else if (sessionIdError === SSOError.INVALID) {
        return 'Your session is invalid. Please refresh the page!'
      } else if (
        ssoErrors?.PHONE_NUMBER?.errorType === SSOError.MOBILE_INVALID
      ) {
        return 'Invalid phone number. Please try again.'
      } else if (
        ssoErrors?.PHONE_SMS_OTP?.errorType ===
        SSOError.SMS_OTP_TOO_MANY_REQUESTS
      ) {
        return 'Sorry, too many requests, please try again later.'
      } else if (
        ssoErrors?.PHONE_SMS_OTP?.errorType === SSOError.SMS_OTP_INCORRECT
      ) {
        return 'Invalid pin entered. Please try again.'
      } else if (
        ssoErrors?.PASSWORD?.errorType === SSOError.PASSWORD_INCORRECT
      ) {
        return 'Password is incorrect. Please try again.'
      } else if (
        ssoErrors?.EMAIL_OTP_CODE?.errorType === SSOError.EMAIL_OTP_INCORRECT
      ) {
        const invalidPinStr = 'Invalid pin entered. Please try again.'
        if (emailHint) {
          return `${invalidPinStr} Did you check ${emailHint} for your code?`
        }
        return invalidPinStr
      } else {
        return defaultErrorMessage
      }
    case 401:
      return defaultErrorMessage
    case 429:
      return 'Sign up rate limit hit! Try again in a few hours.'
    case 500:
      return "Server error! We're working to fix it, try again soon."
    default:
      return 'Something unexpected happened, please try again later.'
  }
}

/**
 * Makes a request to the Uber SSO cloud function. Caches the request body if
 * the request is successful. Returns the response promise. If the fetch call
 * throws an error, it will propagate up, so the caller must handle this.
 */
export const makeRequest = async (
  body: object,
  cacheFunction: (body: object) => void
): Promise<Response> => {
  const response = await fetch(UBER_SSO_URL, {
    ...REQUEST_OPTIONS,
    body: JSON.stringify(body),
  })

  // We cache the request if it's successful. This is important for resend
  // requests, as they can just repeat the last successful request.
  if (response.ok) {
    cacheFunction(body)
  }
  return response
}
